import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { http } from './api';
import { AppCtx } from './AppContext';
import { useT } from './i18n';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Auth from './pages/Auth';
import Boardroom from './pages/Boardroom';
import { resolveEnterpriseV2, resolveProductMode } from './config';
import { resolveExecutionContext } from './app-shell/executionContext';
import { resolveDemoPersona } from './demo/demoAccess';

const IntelligenceSurface = React.lazy(() => import('./surfaces/intelligence/IntelligenceSurface'));
const Chat = React.lazy(() => import('./pages/Chat'));
const ExecutionSurface = React.lazy(() => import('./surfaces/execution/ExecutionSurface'));
const Canvas = React.lazy(() => import('./pages/Canvas'));
const Worktree = React.lazy(() => import('./pages/Worktree'));
const DataSurface = React.lazy(() => import('./surfaces/data/DataSurface'));
const EnterpriseSurface = React.lazy(() => import('./surfaces/enterprise/EnterpriseSurface'));
const NativeWorkspace = React.lazy(() => import('./pages/NativeWorkspace'));
const WorkSurface = React.lazy(() => import('./surfaces/work/WorkSurface'));
const Settings = React.lazy(() => import('./pages/Settings'));

export { AppCtx };

function HomeRoute({ user }) {
  const location = useLocation();
  return <Navigate to={{ pathname: user?.role === 'participant' ? '/work' : '/boardroom', search: location.search }} replace />;
}

export function CanvasRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location?.search || '');
  if (!params.has('view')) {
    params.set('view', 'workspace');
  }
  return <Navigate to={{ pathname: '/execution', search: params.toString() }} replace />;
}

export function WorktreeRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location?.search || '');
  if (!params.has('view')) {
    params.set('view', 'map');
  }
  return <Navigate to={{ pathname: '/execution', search: params.toString() }} replace />;
}

function WorkspaceRedirect({ view }) {
  const { workspaceId, stageId } = useParams();
  const suffix = view === 'canvas'
    ? '/canvas'
    : view === 'stages'
      ? '/stages'
      : view === 'stage'
        ? `/stages/${stageId}`
        : view === 'steward'
          ? '/steward'
          : view === 'boardroom-summary'
            ? '/boardroom-summary'
            : '';
  return <Navigate to={`/work/${workspaceId}${suffix}`} replace />;
}

export default function App() {
  const [user, setUser] = useState(() => {
    const productMode = resolveProductMode();
    const demoPersona = resolveDemoPersona(typeof window !== 'undefined' ? window.location.search : '', productMode);
    if (demoPersona) {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem('tkp_demo_user', JSON.stringify(demoPersona));
      }
      return demoPersona;
    }

    if (productMode === 'DEMO' && typeof window !== 'undefined' && window.sessionStorage) {
      const sessionDemo = sessionStorage.getItem('tkp_demo_user');
      if (sessionDemo) {
        try { return JSON.parse(sessionDemo); } catch {}
      }
    }


    const raw = localStorage.getItem('tkp_user');
    if (!raw) return null;
    try {
      const stored = JSON.parse(raw);
      return stored?.demo ? null : stored;
    } catch {
      return null;
    }
  });
  const [locale, setLocale] = useState('en');
  const [timeRange, setTimeRange] = useState(() => parseInt(localStorage.getItem('tkp_timerange') || '14', 10));
  const [search, setSearch] = useState('');
  const [programId, setProgramId] = useState(() => localStorage.getItem('tkp_program_id') || '');
  const [productionPrograms, setProductionPrograms] = useState([]);
  const t = useT(locale);

  useEffect(() => { localStorage.setItem('tkp_locale', locale); }, [locale]);
  useEffect(() => { localStorage.setItem('tkp_timerange', String(timeRange)); }, [timeRange]);
  useEffect(() => {
    if (programId) localStorage.setItem('tkp_program_id', programId);
  }, [programId]);
  useEffect(() => {
    if (user && !user.demo) localStorage.setItem('tkp_user', JSON.stringify(user));
  }, [user]);

  useEffect(() => {
    if (!user || user.demo) return;
    http.get('/auth/me').then(r => setUser(r.data)).catch(() => {});
  }, []);  // eslint-disable-line

  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener('talkto-auth-expired', onExpired);
    return () => window.removeEventListener('talkto-auth-expired', onExpired);
  }, []);

  const login = (token, u) => {
    localStorage.setItem('tkp_token', token);
    localStorage.setItem('tkp_user', JSON.stringify(u));
    setUser(u);
  };
  const logout = () => {
    localStorage.removeItem('tkp_token');
    localStorage.removeItem('tkp_user');
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('tkp_demo_user');
    }
    setUser(null);
  };


  const productMode = resolveProductMode();
  const enterpriseV2 = user?.demo ? false : resolveEnterpriseV2();

  useEffect(() => {
    if (!user || user.demo || productMode !== 'PRODUCTION') return;
    http.get('/v1/enterprises/projects')
      .then(r => {
        if (Array.isArray(r.data)) setProductionPrograms(r.data);
      })
      .catch(() => {});
  }, [user, productMode]);

  const execCtx = resolveExecutionContext({
    user,
    productMode,
    productionPrograms,
    programId,
    timeRangeDays: timeRange,
    locale,
  });

  const ctx = {
    user, setUser, login, logout,
    locale, setLocale, t,
    timeRange, setTimeRange,
    search, setSearch,
    enterpriseV2, productMode,
    ...execCtx,
    setProgramId,
    setTimeRangeDays: setTimeRange,
  };

  return (
    <AppCtx.Provider value={ctx}>
      <Toaster position="top-right" richColors closeButton duration={3000} />
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
            <Route path="/auth" element={user ? <Navigate to={user.role === 'participant' ? '/work' : '/boardroom'} /> : <Auth />} />
            <Route element={user ? <Layout /> : <Navigate to="/auth" />}>
              <Route path="/" element={<HomeRoute user={user} />} />
              <Route path="/boardroom" element={<Boardroom />} />
              <Route path="/work" element={<WorkSurface />} />
              <Route path="/work/:workspaceId" element={<NativeWorkspace />} />
              <Route path="/work/:workspaceId/canvas" element={<NativeWorkspace />} />
              <Route path="/work/:workspaceId/stages" element={<NativeWorkspace />} />
              <Route path="/work/:workspaceId/stages/:stageId" element={<NativeWorkspace />} />
              <Route path="/work/:workspaceId/steward" element={<NativeWorkspace />} />
              <Route path="/work/:workspaceId/boardroom-summary" element={<NativeWorkspace />} />
              <Route path="/intelligence" element={<IntelligenceSurface />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/execution" element={<ExecutionSurface />} />
              <Route path="/canvas" element={<CanvasRedirect />} />
              <Route path="/worktree" element={<WorktreeRedirect />} />
              <Route path="/data" element={<DataSurface />} />
              <Route path="/enterprise" element={<EnterpriseSurface />} />
              <Route path="/settings" element={<Settings />} />

              <Route path="/my-work" element={<Navigate to="/work?view=my-work" replace />} />
              <Route path="/workspace" element={<Navigate to="/work?view=workflow" replace />} />
              <Route path="/workspace/:workspaceId" element={<WorkspaceRedirect />} />
              <Route path="/workspace/:workspaceId/canvas" element={<WorkspaceRedirect view="canvas" />} />
              <Route path="/workspace/:workspaceId/stages" element={<WorkspaceRedirect view="stages" />} />
              <Route path="/workspace/:workspaceId/stages/:stageId" element={<WorkspaceRedirect view="stage" />} />
              <Route path="/workspace/:workspaceId/steward" element={<WorkspaceRedirect view="steward" />} />
              <Route path="/workspace/:workspaceId/boardroom-summary" element={<WorkspaceRedirect view="boardroom-summary" />} />
              <Route path="/actions" element={<Navigate to="/work?view=actions" replace />} />
              <Route path="/decision" element={<Navigate to="/work?view=decisions" replace />} />
              <Route path="/signals" element={<Navigate to="/intelligence?view=signals" replace />} />
              <Route path="/global" element={<Navigate to="/intelligence?view=risks" replace />} />
              <Route path="/rag" element={<Navigate to="/intelligence?view=knowledge" replace />} />
              <Route path="/integrations" element={<Navigate to="/data?view=sources&connect=true" replace />} />
              <Route path="/integrations/catalog" element={<Navigate to="/data?view=sources&section=catalog" replace />} />
              <Route path="/integrations/instances" element={<Navigate to="/data?view=sources&section=instances" replace />} />
              <Route path="/integrations/sync-runs" element={<Navigate to="/data?view=sources&section=sync-runs" replace />} />
              <Route path="/integrations/import" element={<Navigate to="/data?view=imports" replace />} />
              <Route path="/integrations/mapping" element={<Navigate to="/data?view=sources&section=mapping" replace />} />
              <Route path="/ingestion" element={<Navigate to="/data?view=imports" replace />} />
              <Route path="/admin/enterprise" element={<Navigate to="/enterprise" replace />} />
              <Route path="/admin/enterprise/projects" element={<Navigate to="/enterprise?view=organization&section=projects" replace />} />
              <Route path="/admin/enterprise/workflows" element={<Navigate to="/enterprise?view=governance&section=workflows" replace />} />
              <Route path="/admin/enterprise/integrations" element={<Navigate to="/enterprise?view=operations&section=integrations" replace />} />
              <Route path="/admin/enterprise/evidence" element={<Navigate to="/enterprise?view=governance&section=evidence" replace />} />
              <Route path="/admin/enterprise/readiness" element={<Navigate to="/enterprise?view=governance&section=readiness" replace />} />
              <Route path="/admin/enterprise/capabilities" element={<Navigate to="/enterprise?view=governance&section=capabilities" replace />} />
              <Route path="/admin/enterprise/completion" element={<Navigate to="/enterprise?view=governance&section=completion" replace />} />
              <Route path="/admin/enterprise/support" element={<Navigate to="/enterprise?view=operations&section=support" replace />} />
              <Route path="/tenants" element={<Navigate to="/enterprise?view=organization" replace />} />
              <Route path="/system" element={<Navigate to="/enterprise?view=operations" replace />} />
              <Route path="/audit" element={<Navigate to="/enterprise?view=audit" replace />} />
              <Route path="/notifications" element={<Navigate to="/enterprise?view=notifications" replace />} />
              <Route path="*" element={<Navigate to={user?.role === 'participant' ? '/work' : '/boardroom'} />} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AppCtx.Provider>
  );
}
